import React, { useMemo, useState } from 'react';

import { Box, Button, Divider, Stack, Typography } from '@mui/material';

import TabPanelShell from '~/components/Offers/Detail/OfferBuilder/shared/TabPanelShell';

import { usePricingToolTabCount } from '../../PricingToolTabCounts';
import SectionLabel from '../../shared/SectionLabel';

import OtaRateShopForm from './OtaRateShopForm';
import RawCsvDataTable from './RawCsvDataTable';
import ScrapeCsvTable from './ScrapeCsvTable';
import ScrapePrvSnapshot from './ScrapePrvSnapshot';
import StayInputsForm from './StayInputsForm';
import { computeDailyDiscountStats } from './otaParity/computeGenuineDiscount';
import { computeDailyMarketStats } from './otaParity/computeOtaMarketStats';
import { StayInputOverrides } from './otaParity/types';
import { useOtaRateShopOrchestration } from './useOtaRateShopOrchestration';

export default function ScrapeTab() {
  const [stayInputs, setStayInputs] = useState<StayInputOverrides | null>(null);
  const [rawDataOpen, setRawDataOpen] = useState(false);

  const { status, progressText, error, clearError, parsedCsv, submitScrape, loadMostRecentJob } =
    useOtaRateShopOrchestration();

  const isWorking = status === 'submitting' || status === 'scraping';

  const marketStats = useMemo(
    () => (parsedCsv ? computeDailyMarketStats(parsedCsv, undefined, stayInputs?.blackoutDates) : []),
    [parsedCsv, stayInputs?.blackoutDates],
  );

  const discountStats = useMemo(
    () => (stayInputs ? computeDailyDiscountStats(marketStats, stayInputs) : []),
    [marketStats, stayInputs],
  );

  usePricingToolTabCount('scrape', parsedCsv?.otaColumns.length);

  return (
    <TabPanelShell
      title="Scrape"
      subtitle="Run a new OTA rate scrape or load the most recent one. Review the rate table before continuing to package options."
    >
      <Stack spacing={3}>
        <Box>
          <SectionLabel>Stay inputs</SectionLabel>
          <StayInputsForm onChange={setStayInputs} />
        </Box>

        <Divider />

        <Box>
          <SectionLabel>OTA rate shop</SectionLabel>
          <OtaRateShopForm
            stayInputs={stayInputs}
            isWorking={isWorking}
            progressText={progressText}
            error={error}
            clearError={clearError}
            submitScrape={submitScrape}
            loadMostRecentJob={loadMostRecentJob}
          />

          {parsedCsv && (
            <Button variant="text" sx={{ mt: 2 }} onClick={() => setRawDataOpen(true)}>
              Open full CSV view
            </Button>
          )}

          {parsedCsv && stayInputs && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
                {parsedCsv.rows.length} rows loaded
              </Typography>
              <ScrapeCsvTable
                parsed={parsedCsv}
                marketStats={marketStats}
                discountStats={discountStats}
                currencyCode={stayInputs.currencyCode}
                maxHeight={780}
              />
            </Box>
          )}
        </Box>

        {parsedCsv && stayInputs && (
          <>
            <Divider />
            <ScrapePrvSnapshot parsed={parsedCsv} marketStats={marketStats} discountStats={discountStats} />
          </>
        )}

        {parsedCsv && stayInputs && (
          <RawCsvDataTable
            open={rawDataOpen}
            onClose={() => setRawDataOpen(false)}
            parsed={parsedCsv}
            marketStats={marketStats}
            discountStats={discountStats}
            currencyCode={stayInputs.currencyCode}
          />
        )}
      </Stack>
    </TabPanelShell>
  );
}
